<template>
    <v-container class="pa-5" fluid>
        <SingleNoteDialog
            :note="singleNote"
            :comment="noteComment"
            :is-open="createSingleNoteDialog"
            @create-single-note="createSingleNote"
            @close-single-note-dialog="closeCreateSingleNoteDialog"
            @create-single-note-comment="createSingleNoteComment"
        />

        <v-dialog
            :value="isOpen"
            max-width="1400px"
            transition="slide-y-transition"
            eager
            @keydown.esc="onClose"
            @click:outside="onClose"
        >
            <v-card>
                <v-card-title>
                    <h1 class="text-subtitle-1">
                        Notki stanowiskowe
                    </h1>
                </v-card-title>
                <v-card-actions>
                    <v-col cols="12">
                        <v-btn
                            :loading="actionLoading.noteLoading"
                            color="secondary"
                            medium
                            data-sentry-label="Dodaj notkę admin"
                            @click="showCreateSingleNoteDialog"
                        >
                            <v-icon left>
                                mdi-plus
                            </v-icon>
                            Dodaj notkę
                        </v-btn>
                    </v-col>
                </v-card-actions>
                <v-card-text>
                    <v-data-table
                        :headers="headers"
                        :items="workspaceNotesListData"
                        :mobile-breakpoint="0"
                        name="notesData"
                    >
                        <template #item.title="{item}">
                            {{ item.title }}
                        </template>
                        <template #item.createdAt="{item}">
                            {{ formatDate(item.createdAt) }}
                        </template>
                        <template #item.createdBy="{item}">
                            <template v-if="item.createdBy.firstName && item.createdBy.lastName">
                                {{ item.createdBy.firstName }} {{ item.createdBy.lastName }}
                            </template>
                            <template v-else>
                                -
                            </template>
                        </template>
                        <template #item.approved="{item}">
                            <template v-if="item.approved">
                                <strong class="green--text text--lighten-1">publiczna</strong>
                            </template>
                            <template v-else>
                                <strong class="red--text text--lighten-1">niepubliczna</strong>
                            </template>
                        </template>
                        <template #item.actions="{item}">
                            <td>
                                <div class="d-inline-flex">
                                    <v-tooltip :open-delay="tooltipDelay" top>
                                        <template #activator="{on}">
                                            <v-btn
                                                icon
                                                data-sentry-label="Szczegóły notki"
                                                v-on="on"
                                                @click="getSingleNote(item.id)"
                                            >
                                                <v-icon color="blue">
                                                    mdi-ballot
                                                </v-icon>
                                            </v-btn>
                                        </template>
                                        <span>Szczegóły</span>
                                    </v-tooltip>
                                </div>
                            </td>
                        </template>
                        <template slot="no-results" :value="true">
                            <p class="text-center">
                                Brak danych
                            </p>
                        </template>
                    </v-data-table>
                </v-card-text>
                
                <v-card-actions>
                    <v-btn
                        color="error"
                        text
                        data-sentry-label="Zamknij"
                        @click="onClose"
                    >
                        Zamknij
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </v-container>
</template>

<script>
import dateFormatter from '@/mixins/dateFormatter'
import tooltipDelay from '@/mixins/tooltip'
import parse from '@/mixins/parse.js'
import SingleNoteDialog from './SingleNoteDialog'
import { errorHandler, addSentryBreadcrumb } from '@/utils/errorHandler'

export default {
    name: 'WorkspaceNotesListDialog',
    components: {
        SingleNoteDialog
    },
    mixins: [
        dateFormatter,
        tooltipDelay,
        parse
    ],
    props: {
        isOpen: {
            type: Boolean
        },
        workspaceNotesListData: {
            type: Array,
            default: () => []
        },
    },
    data() {
        return {
            singleNote: {
                id: null,
                title: null,
                text: null,
                approved: false,
                createdAt: null,
                createdBy: null,
                expireDateTime: null,
                read: false,
                productionSiteNoteComments: [],
                originProductionSiteId: null,
                productionSites: [],
                canComment: false
            },
            noteComment: {
                id: null,
                productionSiteNoteId: null,
                comment: null
            },
            createSingleNoteDialog: false,
            actionLoading: {
                noteLoading: false,
                createNote: false,
            },
            headers: [
                {
                    text: 'Tytuł',
                    value: 'title',
                    sortable: false,
                },
                {
                    text: 'Data utworzenia',
                    value: 'createdAt',
                    align: 'center',
                    sortable: false,
                },
                {
                    text: 'Zgłaszający',
                    value: 'createdBy',
                    align: 'center',
                    width: 180,
                    sortable: false,
                },
                {
                    text: 'Widoczność',
                    value: 'approved',
                    align: 'right',
                    sortable: false,
                },
                {
                    text: 'Akcje',
                    value: 'actions',
                    align: 'center',
                    sortable: false,
                    width: 50
                }
            ]
        }
    },
    methods: {
        onClose() {
            this.$emit('on-close')
        },
        getSingleNote(id) {
            this.$store.dispatch('notes/getProductionSiteNote', id)
                .then(response => {
                    this.singleNote = response
                    this.createSingleNoteDialog = true
                }).catch(error => {
                    errorHandler(error)
                })
        },
        showCreateSingleNoteDialog() {
            const workspace = JSON.parse(localStorage.getItem('workspace'))

            this.singleNote.originProductionSiteId = workspace.workspaceId
            this.createSingleNoteDialog = true

            addSentryBreadcrumb('ui.action', 'Tworzenie notki')
        },
        createSingleNote() {
            this.$store.dispatch('notes/createProductionSiteNote', this.singleNote)
                .then((response) => {
                    this.$store.state.notes.productionSiteNotes.productionSiteNotes.unshift(response.data)
                    this.closeCreateSingleNoteDialog()
                })
                .catch(error => {
                    this.closeCreateSingleNoteDialog()
                    errorHandler(error)
                })
        },
        createSingleNoteComment() {
            this.actionLoading.addComment = true
            this.noteComment.productionSiteNoteId = this.singleNote.id
            this.$store.dispatch('notes/createProductionSiteNoteComment', this.noteComment)
                .then((response) => {
                    this.singleNote.productionSiteNoteComments.unshift(response.data)
                    this.noteComment = {productionSiteNoteId: null, comment: null}
                    this.singleNote.canComment = false
                })
                .catch(error => {
                    errorHandler(error)
                })
                .finally(() => {
                    this.actionLoading.createComment = false
                })
        },
        closeCreateSingleNoteDialog() {
            this.createSingleNoteDialog = false

            setTimeout(() => {
                this.singleNote.id = null
                this.singleNote.title = null
                this.singleNote.text = null
                this.singleNote.approved = false
                this.singleNote.createdAt = null
                this.singleNote.createdBy = null
                this.singleNote.expireDateTime = null
                this.singleNote.read = false
                this.singleNote.productionSiteNoteComments = [],
                this.singleNote.originProductionSiteId = null
            }, 500)
        },
    }
}

</script>